import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../../contexts/SessionContext';
import API_URL from '../../../constants/environment';
import { OrderStatus } from '../../../hooks/useGetEms';

export type { OrderStatus } from '../../../hooks/useGetEms';

export interface OrderFeed {
    _id: string;
    title: string;
    vin: string;
    insurance: string | null;
    customer: string | null;
    numParts: number;
    received: Date;
    status: OrderStatus;
    location: string;
}

interface OrderFeedResponse {
    desktopHeartbeatInvalid: boolean;
    latestDesktopHeartbeat: Date | null;
    lateHeartbeatLocations: Array<string>;
    orders: Array<OrderFeed>;
}

interface OrderFeedInput {
    type: OrderStatus | 'all';
    limit: number;
    offset: number;
    locationIds: Array<string> | 'all';
    search?: string;
}

const getOrdersFeed = async (sessionId: string, type: OrderStatus | 'all', locationIds: Array<string> | 'all', offset: number): Promise<OrderFeedResponse> => {
    const { data } = await axios.post(`${API_URL}/ems/orders/feed`,
        {
            type,
            limit: 20,
            offset,
            locationIds
        } satisfies OrderFeedInput,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useExportsFeed = (type: OrderStatus | 'all' = 'all', locationIds: Array<string> | 'all') => {
    const sessionContext = useContext(SessionContext);
    return useInfiniteQuery({
        queryKey: ['/ems/orders/feed', type, JSON.stringify(locationIds)],
        queryFn: ({ pageParam = 0 }) => getOrdersFeed(sessionContext?.sessionId ?? '', type, locationIds, pageParam),
        getNextPageParam: (lastPage, pages) => {
            if (lastPage.orders.length === 20) {
                return pages.length * 20;
            }
            return undefined;
        },
        initialPageParam: 0,
    });
};

const getOrdersFeedPaginated = async (
    sessionId: string,
    type: OrderStatus | 'all',
    locationIds: Array<string> | 'all',
    page: number,
    limit: number,
    search: string
): Promise<OrderFeedResponse> => {
    const offset = (page - 1) * limit;
    const { data } = await axios.post(`${API_URL}/ems/orders/feed`,
        {
            type,
            limit,
            offset,
            locationIds,
            search
        } satisfies OrderFeedInput,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useExportsFeedPaginated = (
    type: OrderStatus | 'all' = 'all',
    locationIds: Array<string> | 'all',
    page: number = 1,
    limit: number = 20,
    search: string = ''
) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['/ems/orders/feed-paginated', type, JSON.stringify(locationIds), page, limit, search],
        queryFn: () => getOrdersFeedPaginated(sessionContext?.sessionId ?? '', type, locationIds, page, limit, search),
        enabled: !!sessionContext?.sessionId && (locationIds === 'all' || locationIds.length > 0),
        refetchInterval: 1000 * 60,
    });
}; 